import constants from '#packages/constants';
import { domMeasurements } from '#packages/stateManagement';
import { extraHeight } from '#packages/util';
import { createSnapHandler } from './resizeGapsSnapHandler';
import { createTransition } from './resizeGapsTransition';
import { reportGapResizedBI } from './resizeGapsBI';

import type { ResizeGapsScope } from './resizeGapsScope';
import type { MouseEvent } from 'react';
import type { Gap } from './resizeGapsTypes';

const { translateToViewerCoordinates, getStageLayout } =
  domMeasurements.selectors;

export const resizeImmediately = (
  scope: ResizeGapsScope,
  gap: Gap,
  newHeight: number,
) => {
  const { resizeGapsStore } = scope;
  const transition = createTransition(scope, gap);

  const deltaY = newHeight - gap.layout.height;
  transition.update({ deltaY });
  resizeGapsStore.setGapHeight(newHeight);
  transition.end();
};

export const createMouseHandler = (
  scope: ResizeGapsScope,
  e: MouseEvent,
  gap: Gap,
) => {
  const { editorAPI, resizeGapsStore } = scope;

  const snapChecker = createSnapHandler(editorAPI, gap);
  const transition = createTransition(scope, gap);
  const initMouseCoords = translateToViewerCoordinates(
    editorAPI,
    e,
  ).relativeToSite;
  const { increaseExtraHeight, rollbackExtraHeight } =
    extraHeight.createHelpersForSafeResize(editorAPI);

  const biParams = {
    initialGap: gap,
    updatedGap: gap,
    isSnapped: false,
  };

  return {
    start: async () => {
      resizeGapsStore.setIsResizing(true);
      await increaseExtraHeight(
        gap.container,
        getStageLayout(editorAPI.store.getState()).height,
      );
    },
    on: (event: MouseEvent) => {
      const mouseCoords = translateToViewerCoordinates(
        editorAPI,
        event,
      ).relativeToSite;
      const minDeltaYPossible = -gap.layout.height;
      const deltaY = Math.max(
        minDeltaYPossible,
        (mouseCoords.y - initMouseCoords.y) * 2,
      );

      const { snapData, deltaY: newDeltaY } =
        snapChecker.getSnapDataAndModifiedDeltaY(deltaY);
      editorAPI.snapData.set(snapData);

      transition.update({ deltaY: newDeltaY });
      resizeGapsStore.setGapHeight(gap.layout.height + newDeltaY);
      biParams.isSnapped = deltaY !== newDeltaY;
      biParams.updatedGap = resizeGapsStore.getGap();
    },
    end: async () => {
      editorAPI.snapData.clear();
      transition.end();
      await rollbackExtraHeight();
      resizeGapsStore.setIsResizing(false);
      reportGapResizedBI(editorAPI, biParams);
    },
    type: constants.MOUSE_ACTION_TYPES.GAP_RESIZE_PUSH,
  };
};
